import Dispatcher from '../events.js'
import { createApp, h, reactive, markRaw } from 'vue'
import { registerIcons } from '@/icons'

function OverlayWrapper(map, {buttons=[], options={}, classes=[]} = {}) {
  this._cmpInstance = null;
  this._app = null;
  this._destroyed = false; // Track if overlay has been destroyed

  this.buttons = buttons;
  this.options = options;

  // Create reactive props object for Vue 3
  this._reactiveProps = reactive({
    overlay: this,
    buttons: this.buttons,
    options: this.options,
    x: 0,
    y: 0,
    text: '',
    visible: false
  });

  // Throttling for draw method
  this._drawScheduled = false;

  classes.unshift('custom-overlay');
  this._additionalClasses = classes;

  this.dispatcher = new Dispatcher();
  this.setMap(map);
}

// OverlayWrapper.prototype = new google.maps.OverlayView();
OverlayWrapper.prototype = Object.create(google.maps.OverlayView.prototype);

OverlayWrapper.prototype.onAdd = function() {
  // Prevent re-adding if overlay has been destroyed
  if (this._destroyed) {
    console.warn('OverlayWrapper.onAdd(): BLOCKED - overlay was destroyed', this.options);
    return;
  }

  console.log('OverlayWrapper.onAdd() called - creating overlay DOM', this.options);
  let panes = this.getPanes();
  let float = panes.floatPane;

  let container = document.createElement('div');
  container.style.position = 'absolute';
  container.draggable = true;

  // Vue 3: Create app instance with component using reactive props
  const self = this;
  this._app = createApp({
    render() {
      return h(self.cmp, {
        ...self._reactiveProps,
        onOverlayEvent: self.handleOverlayEvent.bind(self)
      });
    }
  });

  // Register icons for this app instance
  registerIcons(this._app);

  // Mount to a temporary div
  const mountPoint = document.createElement('div');
  this._cmpInstance = this._app.mount(mountPoint);

  container.appendChild(mountPoint);
  container.classList.add(...this._additionalClasses);

  float.appendChild(container);

  this._container = container;

  this.buttons.forEach((btn) => {
    btn.behavior.setup.call(this, btn);
  });

  this.dispatcher.dispatch('add');
}

OverlayWrapper.prototype.handleOverlayEvent = function(eventName, btn, e) { 
  if (this._visible) {
    // console.log('in overlayevent', e);

    let mappedEvent = btn.behavior.handleEvent(eventName, e);
    let data = e;
    
    if (mappedEvent) {      
      if (mappedEvent in btn.behavior) {
        data = btn.behavior[mappedEvent].call(this, btn, e);
      }
      
      if (data !== false) {
        this.dispatcher.dispatch(`${btn.name}-${mappedEvent}`, data);
        // console.log(`eventName: ${eventName} btn: ${btn.name}`);
      }
    }
  }
}

OverlayWrapper.prototype.onRemove = function() {
  console.log('OverlayWrapper.onRemove() called', this.options);

  // Mark as destroyed to prevent re-adding
  this._destroyed = true;

  if (this._app) {
    this._app.unmount();
    this._app = null;
    this._cmpInstance = null;
  }
  if (this._container) {
    const parent = this._container.parentNode;
    const classes = this._container.className;
    console.log('OverlayWrapper.onRemove(): Container classes:', classes, 'Parent:', parent ? 'exists' : 'null');

    if (parent) {
      parent.removeChild(this._container);
      console.log('OverlayWrapper.onRemove(): Container removed from DOM');
    } else {
      console.warn('OverlayWrapper.onRemove(): Container has no parent - already removed?');
    }
    delete this._container;
  } else {
    console.log('OverlayWrapper.onRemove(): No container to remove');
  }
}

OverlayWrapper.prototype.draw = function() {
  if (!this._container || !this.get('position')) {
    console.log('OverlayWrapper.draw(): Skipped - no container or position', this.options);
    return;
  }

  console.log('OverlayWrapper.draw(): Drawing overlay', this.options?.shapeId);

  // Use requestAnimationFrame to throttle draw calls for better performance
  if (this._drawScheduled) {
    return;
  }

  this._drawScheduled = true;
  requestAnimationFrame(() => {
    this._drawScheduled = false;

    if (!this._container || !this.get('position')) {
      return;
    }

    let projection = this.getProjection();
    let {x, y} = projection.fromLatLngToDivPixel(this.get('position'));

    // Use transform for better performance during dragging
    // transform doesn't trigger layout/reflow like top/left does
    this._container.style.transform = `translate(${x}px, ${y}px)`;
    this._container.style.top = '0px';
    this._container.style.left = '0px';

    // Don't update reactive props for x/y during draw - they're not used in templates
    // and updating them triggers unnecessary re-renders
    // this.$setProp('x', x);
    // this.$setProp('y', y);
  });
}

OverlayWrapper.prototype.$setProp = function(key, val) {
  // In Vue 3, update the reactive props object
  if (this._reactiveProps && key in this._reactiveProps) {
    // Convert to string if the prop is 'text' to match prop type
    if (key === 'text' && typeof val !== 'string') {
      val = String(val);
    }
    this._reactiveProps[key] = val;
  }
}

OverlayWrapper.prototype.setPosition = function(latLng) {
  this.set('position', latLng);  
  let fn = () => this.draw();
  
  if (this._container) return fn();  
}

OverlayWrapper.prototype.setVisible = function(visible) {
  this._visible = ((visible === null || visible)) ? true : false;
  // console.log(visible);
  let fn = () => {    
    this.draw();

    setTimeout(() => this.$setProp('visible', (this._visible)), 100);

    if (this._visible) {
      this._container.style.display = 'block'
      this.set('visible', true);

      if (this._timer) clearTimeout(this._timer);      
    } else {      
      this._timer = setTimeout(() => {
        this.set('visible', false);
        this._container.style.display = 'none';
       }, 400);
    }    
  }

  if (this._container) return fn();
  this.dispatcher.on('add', fn, true);
}

export default OverlayWrapper;